import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Search, PencilRuler, Wrench, Activity } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

export const Process = () => {
  const ref = useRef(null);
  const { t } = useI18n();
  const STEPS = [
    { icon: Search, no: "01", title: t("pr.s1.t"), desc: t("pr.s1.d") },
    { icon: PencilRuler, no: "02", title: t("pr.s2.t"), desc: t("pr.s2.d") },
    { icon: Wrench, no: "03", title: t("pr.s3.t"), desc: t("pr.s3.d") },
    { icon: Activity, no: "04", title: t("pr.s4.t"), desc: t("pr.s4.d") },
  ];
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 65%", "end 60%"] });
  const h = useSpring(scrollYProgress, { stiffness: 90, damping: 25 });

  return (
    <section id="proces" data-testid="process-section" className="relative border-t border-white/10 bg-ink-800">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ • ]">{t("pr.label")}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("pr.h1")} <span className="text-aurora">{t("pr.hkw")}</span>
            </h2>
          </Reveal>
        </div>

        <div ref={ref} className="relative mt-16 pl-14 sm:pl-20">
          {/* track */}
          <div className="absolute left-[26px] top-2 h-[calc(100%-1rem)] w-px bg-white/10 sm:left-[38px]" />
          <motion.div style={{ scaleY: h }} className="absolute left-[26px] top-2 h-[calc(100%-1rem)] w-px origin-top bg-gradient-to-b from-cyan-neon to-[#7C5CFC] sm:left-[38px]" />

          <div className="flex flex-col gap-12 sm:gap-16">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={s.no}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, ease: EASE, delay: i * 0.05 }}
                  className="relative"
                >
                  <span className="absolute -left-14 top-0 grid h-[52px] w-[52px] place-items-center rounded-full border border-cyan-neon/40 bg-ink-900 text-cyan-neon shadow-[0_0_20px_rgba(0,229,255,0.25)] sm:-left-20 sm:h-16 sm:w-16">
                    <Icon size={22} />
                  </span>
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-sm font-bold text-cyan-neon">{s.no}</span>
                    <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">{s.title}</h3>
                  </div>
                  <p className="mt-3 max-w-xl font-body text-base leading-relaxed text-white/55">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
